import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminActiveTasks, useAdminRecentTasks } from '../../hooks/useAdminTasks';
import { useAdminSettings, useFloorWorkerRates } from '../../hooks/useCompanyData';
import { useLabourCostCalculation } from '../../hooks/useLabourCostCalculation';
import { useBestRunBenchmarks } from '../../hooks/useBestRunBenchmarks';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';
import { getDateRange, getComparisonRange, getComparisonLabelKey, formatElapsed, formatDate, formatSmartDateTime, formatTime } from '../../utils/time';
import { formatDuration, formatCurrency, calcLabourCost, isVeryShortCompletedTask } from '../../utils/labour';
import { getBenchmarkKey } from '../../utils/benchmark';
import { useTimer } from '../../hooks/useTimer';
import { format, startOfWeek } from 'date-fns';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { RefreshCw, Search, ChevronUp, ChevronDown, ChevronsUpDown, AlertTriangle, Lightbulb, FileText, Trophy } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import BenchmarkCard from '../../components/common/BenchmarkCard';
import BottleneckCards from '../../components/admin/BottleneckCards';
import { useCompanyTaskNotes } from '../../hooks/useCompanyTaskNotes';
import { detectBottlenecks } from '../../utils/bottlenecks';
import { buildProductionTasksCSV, buildWorkerPerformanceCSV } from '../../utils/exportHelpers';
import { downloadCSV, makeExportFilename } from '../../utils/csv';
import AnalyticsViewsPanel from '../../components/admin/AnalyticsViewsPanel';
import { useAnalyticsViews } from '../../hooks/useAnalyticsViews';
import { useProductionAreas, useAllTaskCategories, useAllTaskTags } from '../../hooks/useCompanyData';
import {
  DEFAULT_ANALYTICS_FILTERS, validateAnalyticsFilters, applyAnalyticsFilters,
  buildPresetFilters, getPresetById, hasActiveFilters, getStaleFilterHints,
} from '../../utils/analyticsFilters';
import './AdminDashboard.css';

const LAST_ANALYTICS_VIEW_KEY = 'florra:lastAnalyticsView';
function lastViewStorageKey(companyId) {
  return `${LAST_ANALYTICS_VIEW_KEY}:${companyId}`;
}

const DATE_PRESETS_SHORT = ['today', 'yesterday', 'thisWeek', 'thisMonth'];
const DATE_PRESETS_LONG = ['last3Months', 'last6Months', 'last365Days'];

const TYPE_COLORS = {
  production: '#2d6a4f',
  support: '#e9850a',
  delay: '#c62828',
  other: '#9ca3af',
};

const CHART_TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 6,
  border: '1px solid var(--border-color)',
  background: 'var(--surface-1)',
  color: 'var(--text-primary)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
};

// ── Helpers ──────────────────────────────────────────────────────

function makeRelTime(t) {
  return (ts) => {
    if (!ts) return '';
    const d = ts?.toDate?.() ?? new Date(ts);
    if (isNaN(d)) return '';
    const s = (Date.now() - d.getTime()) / 1000;
    if (s < 60) return t('admin.justNow');
    if (s < 3600) return `${Math.floor(s / 60)}${t('admin.minutesAgo')}`;
    if (s < 86400) return `${Math.floor(s / 3600)}${t('admin.hoursAgo')}`;
    return `${Math.floor(s / 86400)}${t('admin.daysAgo')}`;
  };
}

function resolveName(str, t) {
  if (!str) return t('dashboard.unknown');
  return str;
}

function trunc(str, t, n = 22) {
  const name = resolveName(str, t);
  return name.length > n ? name.slice(0, n) + '…' : name;
}

function groupByDuration(tasks, keyFn, t, n = 7) {
  const m = {};
  for (const task of tasks) {
    const k = keyFn(task);
    if (!k) continue;
    m[k] = (m[k] || 0) + (task.durationMinutes || 0);
  }
  return Object.entries(m)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, minutes]) => ({ name: trunc(name, t), minutes }));
}

function groupByCost(tasks, keyFn, t, n = 7) {
  const m = {};
  for (const task of tasks) {
    const k = keyFn(task);
    if (!k || task.labourCost == null) continue;
    m[k] = (m[k] || 0) + task.labourCost;
  }
  return Object.entries(m)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, cost]) => ({ name: trunc(name, t), cost }));
}

// Headline figures for a period — used for the current period and to compare
// against the previous one. workerCount = distinct named workers who logged
// work; team-size-only tasks have no identities so they don't add to it.
function periodSummary(tasks) {
  const names = new Set();
  let labourHours = 0;
  let labourCost = 0;
  let costedLabourHours = 0; // hours belonging to costed tasks only
  let hasCost = false;
  let totalUnits = 0;
  let costedUnits = 0; // units on costed tasks only — denominator for cost/unit
  let hasUnits = false;
  let totalDurationMinutes = 0;
  for (const t of tasks) {
    labourHours += t.labourHours || 0;
    totalDurationMinutes += t.durationMinutes || 0;
    if (t.labourCost != null) {
      labourCost += t.labourCost;
      costedLabourHours += t.labourHours || 0;
      costedUnits += t.quantityCompleted || 0;
      hasCost = true;
    }
    if (t.quantityCompleted != null) {
      totalUnits += t.quantityCompleted;
      hasUnits = true;
    }
    for (const n of t.employeeNames || []) names.add(n);
  }
  // Blended effective rate over the costed work only — dividing by total hours
  // (incl. uncosted tasks) would understate it. A shift here over time is the
  // clearest signal of wage changes being applied.
  const avgRate = hasCost && costedLabourHours > 0
    ? parseFloat((labourCost / costedLabourHours).toFixed(2))
    : null;
  // Cost per unit: total known labour cost ÷ units from costed tasks only.
  const costPerUnit = hasCost && costedUnits > 0
    ? parseFloat((labourCost / costedUnits).toFixed(4))
    : null;
  return {
    taskCount: tasks.length,
    workerCount: names.size,
    labourHours,
    labourCost,
    avgRate,
    hasCost,
    totalUnits,
    hasUnits,
    costPerUnit,
    avgDuration: tasks.length > 0 ? parseFloat((totalDurationMinutes / tasks.length).toFixed(1)) : null,
  };
}

// Percent change from prev→curr. null when there's no meaningful baseline.
function pctChange(curr, prev) {
  if (prev == null || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
}

// ── Delta indicator ───────────────────────────────────────────────
// goodWhenDown=true flips the colour semantics (used for cost: a drop is good).
function Delta({ pct, goodWhenDown = false }) {
  if (pct == null) return null;
  const flat = Math.abs(pct) < 0.05;
  const up = pct > 0;
  const tone = flat ? 'flat' : (up !== goodWhenDown ? 'good' : 'bad');
  const arrow = flat ? '→' : up ? '▲' : '▼';
  return (
    <span className={`kpi-delta kpi-delta-${tone}`}>
      {arrow} {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

// ── Sortable column header ────────────────────────────────────────

function SortIcon({ col, sortKey, sortDir }) {
  if (col !== sortKey) return <ChevronsUpDown size={12} className="sort-icon-dim" />;
  return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
}

function Th({ label, col, sortKey, sortDir, onSort, right }) {
  return (
    <th className={`th-sort${right ? ' text-right' : ''}`} onClick={() => onSort(col)}>
      <span className="th-inner">
        {label}
        <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
      </span>
    </th>
  );
}

// ── Active task row (live timer, pause-aware, with cost) ──────────

function ActiveRow({ task, workerRateMap, defaultTeamRate, currency, t }) {
  const isPaused = !!task.pausedAt;
  const prevPausedSeconds = (task.pausedMinutes ?? 0) * 60;

  const timerElapsed = useTimer(
    task.startTime?.toDate?.() ?? task.startTime,
    !isPaused,
    prevPausedSeconds,
  );

  const frozenSeconds = useMemo(() => {
    if (!isPaused || !task.pausedAt || !task.startTime) return 0;
    const pausedAt = task.pausedAt.toDate ? task.pausedAt.toDate() : new Date(task.pausedAt);
    const startTime = task.startTime.toDate ? task.startTime.toDate() : new Date(task.startTime);
    return Math.max(0, Math.floor((pausedAt - startTime) / 1000) - prevPausedSeconds);
  }, [isPaused, task.pausedAt, task.startTime, prevPausedSeconds]);

  const elapsed = isPaused ? frozenSeconds : timerElapsed;
  const isStuck = elapsed > 8 * 3600;

  const liveCost = useMemo(() => {
    if (!workerRateMap || elapsed <= 0) return null;
    const { cost } = calcLabourCost(
      elapsed / 60,
      task.employeeIds,
      task.teamSize,
      workerRateMap,
      defaultTeamRate,
    );
    return cost;
  }, [elapsed, task.employeeIds, task.teamSize, workerRateMap, defaultTeamRate]);

  const startedAt = task.startTime?.toDate?.() ?? (task.startTime ? new Date(task.startTime) : null);
  const statusVariant = isStuck ? 'stuck' : isPaused ? 'paused' : 'running';
  const statusLabel = isStuck
    ? t('dashboard.stuckStatus')
    : isPaused
    ? t('dashboard.pausedStatus')
    : t('dashboard.runningStatus');
  const displayName = task.isCustomTaskName ? task.customTaskName : task.taskName;

  return (
    <tr className={isStuck ? 'row-stuck' : undefined}>
      <td>
        <div className="font-semibold text-sm">{displayName}</div>
        {task.taskCategoryName
          && task.taskCategoryName.toLowerCase() !== displayName?.toLowerCase() && (
          <div className="text-xs text-muted">{task.taskCategoryName}</div>
        )}
      </td>
      <td><span className={`badge badge-${task.taskType}`}>{task.taskType}</span></td>
      <td>
        <span className={`status-chip status-${statusVariant}`}>{statusLabel}</span>
      </td>
      <td className="text-sm">{task.employeeNames?.join(', ') || t('dashboard.teamX', { size: task.teamSize })}</td>
      <td className="text-sm text-muted">{task.roomName || '—'}</td>
      <td className="text-sm text-muted">{startedAt ? formatTime(startedAt) : '—'}</td>
      <td
        className="font-semibold"
        style={{
          fontVariantNumeric: 'tabular-nums',
          color: isStuck
            ? 'var(--color-danger, #c62828)'
            : isPaused
            ? 'var(--text-muted)'
            : 'var(--color-brand)',
        }}
      >
        {formatElapsed(elapsed)}
      </td>
      <td className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {liveCost != null ? (
          <span className="live-cost-value">{formatCurrency(liveCost, currency)}</span>
        ) : (
          <span className="text-muted text-xs">—</span>
        )}
      </td>
    </tr>
  );
}

// ── Trend tooltip ─────────────────────────────────────────────────

function TrendTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ ...CHART_TOOLTIP_STYLE, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{formatDuration(d.minutes)}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
        {t('productionMeta.taskCount', { count: d.count })}
      </div>
    </div>
  );
}

// ── Cost trend tooltip ────────────────────────────────────────────

function CostTrendTooltip({ active, payload, label, currency }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{ ...CHART_TOOLTIP_STYLE, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{formatCurrency(d.cost, currency)}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
        {t('productionMeta.taskCount', { count: d.count })} · {parseFloat((d.minutes / 60).toFixed(1))}h
      </div>
    </div>
  );
}

// ── Smart insight generation ──────────────────────────────────────
// Pure function — takes computed stats + comparison data, returns up to 4 insight objects.
// Each insight has: { id, priority, type, title, metric, body, rec }
// type: 'good' | 'warning' | 'danger' | 'info'
// priority: lower = more important (used for sort before capping at 4)

function generateInsights({ stats, comparison, completedTasks, comparisonLabel, currency, t }) {
  const taskCount = completedTasks.length;
  if (taskCount < 3) return [];

  const { curr, hasPrev, costPerUnitPct, avgRatePct } = comparison;
  const insights = [];

  // 1. Cost per unit trend — highest-value business signal
  if (hasPrev && costPerUnitPct != null && Math.abs(costPerUnitPct) >= 2 && curr.costPerUnit != null && taskCount >= 5) {
    const rising = costPerUnitPct > 0;
    insights.push({
      id: 'cost-per-unit',
      priority: rising ? 1 : 3,
      type: rising ? 'warning' : 'good',
      title: rising
        ? t('dashboard.insights.costPerUnitRisingTitle')
        : t('dashboard.insights.costPerUnitImprovingTitle'),
      metric: formatCurrency(curr.costPerUnit, currency),
      body: t('dashboard.insights.costPerUnitBody', {
        pct: Math.abs(costPerUnitPct).toFixed(0),
        direction: rising ? t('dashboard.insights.higher') : t('dashboard.insights.lower'),
        period: comparisonLabel,
      }),
      rec: rising
        ? t('dashboard.insights.costPerUnitRisingRec')
        : t('dashboard.insights.costPerUnitImprovingRec'),
    });
  }

  // 2. Productivity ratio — % of time on direct production vs support/delay
  if (stats.productivityRatio != null && taskCount >= 5) {
    const r = stats.productivityRatio;
    if (r < 65) {
      insights.push({
        id: 'productivity-low',
        priority: r < 45 ? 0 : 1,
        type: r < 45 ? 'danger' : 'warning',
        title: t('dashboard.insights.productivityLowTitle', { pct: r }),
        metric: `${r}%`,
        body: t('dashboard.insights.productivityLowBody', { other: 100 - r }),
        rec: t('dashboard.insights.productivityLowRec'),
      });
    } else if (r >= 82 && taskCount >= 8) {
      insights.push({
        id: 'productivity-high',
        priority: 5,
        type: 'good',
        title: t('dashboard.insights.productivityHighTitle', { pct: r }),
        metric: `${r}%`,
        body: t('dashboard.insights.productivityHighBody'),
        rec: null,
      });
    }
  }

  // 3. Significant downtime — bottleneck alert
  if (stats.totalDowntime > 0 && stats.totalDuration > 0 && taskCount >= 3) {
    const pct = Math.round((stats.totalDowntime / stats.totalDuration) * 100);
    if (pct >= 10) {
      insights.push({
        id: 'downtime',
        priority: pct >= 25 ? 0 : 2,
        type: pct >= 25 ? 'danger' : 'warning',
        title: t('dashboard.insights.downtimeTitle', { pct }),
        metric: formatDuration(stats.totalDowntime),
        body: t('dashboard.insights.downtimeBody', { duration: formatDuration(stats.totalDowntime) }),
        rec: t('dashboard.insights.downtimeRec'),
      });
    }
  }

  // 4. Long-running outlier tasks — anomaly detection
  if (stats.avgDuration != null && taskCount >= 5) {
    const threshold = stats.avgDuration * 2.5;
    const outliers = completedTasks.filter((task) => (task.durationMinutes || 0) > threshold);
    if (outliers.length >= 2) {
      const catMap = {};
      for (const task of outliers) {
        const k = task.taskCategoryName || t('dashboard.uncategorised');
        catMap[k] = (catMap[k] || 0) + 1;
      }
      const topCat = Object.entries(catMap).sort((a, b) => b[1] - a[1])[0];
      const catNote = topCat && topCat[1] >= 2
        ? t('dashboard.insights.longTasksCatNote', { category: topCat[0] })
        : '';
      insights.push({
        id: 'long-tasks',
        priority: 2,
        type: 'warning',
        title: t('dashboard.insights.longTasksTitle', { count: outliers.length }),
        metric: `>${formatDuration(Math.round(threshold))}`,
        body: t('dashboard.insights.longTasksBody', {
          avg: formatDuration(Math.round(stats.avgDuration)),
        }) + catNote,
        rec: t('dashboard.insights.longTasksRec'),
      });
    }
  }

  // 5. Effective labour rate shift — cost driver signal
  if (hasPrev && avgRatePct != null && Math.abs(avgRatePct) >= 5 && curr.avgRate != null) {
    const up = avgRatePct > 0;
    insights.push({
      id: 'rate-shift',
      priority: 4,
      type: 'info',
      title: up
        ? t('dashboard.insights.rateShiftTitleUp', { pct: Math.abs(avgRatePct).toFixed(0), period: comparisonLabel })
        : t('dashboard.insights.rateShiftTitleDown', { pct: Math.abs(avgRatePct).toFixed(0), period: comparisonLabel }),
      metric: `${formatCurrency(curr.avgRate, currency)}/hr`,
      body: t('dashboard.insights.rateShiftBody', {
        pct: Math.abs(avgRatePct).toFixed(0),
        period: comparisonLabel,
      }),
      rec: up ? t('dashboard.insights.rateShiftRecUp') : null,
    });
  }

  // 6. Top cost category concentration — strategic focus
  if (stats.topCostCategories.length > 0 && curr.hasCost && curr.labourCost > 0 && taskCount >= 5) {
    const top = stats.topCostCategories[0];
    const pct = Math.round((top.cost / curr.labourCost) * 100);
    if (pct >= 40) {
      insights.push({
        id: 'top-cost-category',
        priority: 4,
        type: 'info',
        title: t('dashboard.insights.topCategoryTitle', { name: top.name, pct }),
        metric: formatCurrency(top.cost, currency),
        body: t('dashboard.insights.topCategoryBody'),
        rec: pct >= 60 ? t('dashboard.insights.topCategoryRec') : null,
      });
    }
  }

  // 7. Missing labour rates — data completeness
  if (stats.needsRateCount > 0 && taskCount > 0) {
    const pct = Math.round((stats.needsRateCount / taskCount) * 100);
    if (stats.needsRateCount >= 3 || pct >= 20) {
      insights.push({
        id: 'missing-rates',
        priority: 6,
        type: 'info',
        title: t('dashboard.insights.missingRatesTitle', { count: stats.needsRateCount }),
        metric: t('dashboard.insights.uncostedMetric', { pct }),
        body: t('dashboard.insights.missingRatesBody'),
        rec: t('dashboard.insights.missingRatesRec'),
      });
    }
  }

  insights.sort((a, b) => a.priority - b.priority);
  return insights.slice(0, 4);
}

// ── Insight card ──────────────────────────────────────────────────

function InsightCard({ insight }) {
  const { t } = useTranslation();
  const typeLabel = t(`dashboard.insightType.${insight.type}`, {
    defaultValue: t('dashboard.insightType.default'),
  });
  return (
    <div className={`insight-card insight-card-${insight.type}`}>
      <span className={`insight-type-badge insight-type-badge-${insight.type}`}>{typeLabel}</span>
      <div className="insight-card-header">
        <span className="insight-card-title">{insight.title}</span>
        {insight.metric && (
          <span className="insight-card-metric">{insight.metric}</span>
        )}
      </div>
      <p className="insight-card-body">{insight.body}</p>
      {insight.rec && (
        <p className="insight-card-rec">{insight.rec}</p>
      )}
    </div>
  );
}

// ── Plain-language summary ────────────────────────────────────────
// Returns structured segments [{text, bold}] for rendering with inline bold.

function seg(text, bold = false) {
  return { text, bold };
}

function generatePlainSummary({ datePreset, completedTasks, stats, comparison, comparisonLabel, currency, tagFilterName, t }) {
  const taskCount = completedTasks.length;
  if (taskCount === 0) return null;

  const baseTitle = t(`dashboard.summaryTitle.${datePreset}`, {
    defaultValue: t('dashboard.summaryTitle.default'),
  });
  const title = tagFilterName
    ? t('dashboard.summaryTagSuffix', { title: baseTitle, tag: tagFilterName })
    : baseTitle;
  const { curr, hasPrev, costPerUnitPct, tasksPct, costPct, hoursPct } = comparison;
  const hasCostData = curr.hasCost && stats.totalLabourCost > 0;

  // ── What happened (up to 2 short sentences) ──────────────────────
  const what = [];

  const workers = curr.workerCount;

  if (workers > 0) {
    what.push(seg(t('dashboard.workerCount', { count: workers }), true));
    what.push(seg(t('dashboard.plainSummary.workersCompletedSuffix')));
    what.push(seg(t('dashboard.plainSummary.tasksWord', { count: taskCount }), true));
  } else {
    what.push(seg(t('dashboard.plainSummary.completedTasks')));
    what.push(seg(t('dashboard.plainSummary.tasksWord', { count: taskCount }), true));
  }

  if (stats.totalLabourHours > 0) {
    what.push(seg(t('dashboard.plainSummary.across')));
    what.push(seg(t('dashboard.plainSummary.hoursWord', { hours: stats.totalLabourHours.toFixed(1) }), true));
  }
  what.push(seg('.'));

  // Second sentence: pick the most actionable metric
  const showCostPerUnit =
    hasCostData &&
    curr.costPerUnit != null &&
    hasPrev &&
    costPerUnitPct != null &&
    Math.abs(costPerUnitPct) >= 3;

  if (showCostPerUnit) {
    const dir = costPerUnitPct > 0
      ? t('dashboard.plainSummary.directionUp')
      : t('dashboard.plainSummary.directionDown');
    what.push(seg(t('dashboard.plainSummary.costPerUnitWas')));
    what.push(seg(formatCurrency(curr.costPerUnit, currency), true));
    what.push(seg(`, ${dir} `));
    what.push(seg(`${Math.abs(costPerUnitPct).toFixed(0)}%`, true));
    what.push(seg(t('dashboard.plainSummary.vsPeriod', { period: comparisonLabel })));
  } else if (hasCostData) {
    what.push(seg(t('dashboard.plainSummary.labourCostWas')));
    what.push(seg(formatCurrency(stats.totalLabourCost, currency), true));
    if (hasPrev && costPct != null && Math.abs(costPct) >= 3) {
      const dir = costPct > 0
        ? t('dashboard.plainSummary.directionUp')
        : t('dashboard.plainSummary.directionDown');
      what.push(seg(`, ${dir} `));
      what.push(seg(`${Math.abs(costPct).toFixed(0)}%`, true));
      what.push(seg(t('dashboard.plainSummary.vsPeriod', { period: comparisonLabel }).replace(/\.$/, '')));
    }
    what.push(seg('.'));
  } else if (stats.totalQty > 0) {
    what.push(seg(t('dashboard.plainSummary.totalOutputWas')));
    what.push(seg(t('dashboard.plainSummary.unitsBold', { count: stats.totalQty.toLocaleString() }), true));
    what.push(seg('.'));
  } else if (hasPrev && tasksPct != null && Math.abs(tasksPct) >= 10) {
    const dir = tasksPct > 0
      ? t('dashboard.plainSummary.directionUp')
      : t('dashboard.plainSummary.directionDown');
    what.push(seg(t('dashboard.plainSummary.taskVolumeIs')));
    what.push(seg(t('dashboard.plainSummary.taskVolumeDirPct', {
      dir,
      pct: `${Math.abs(tasksPct).toFixed(0)}%`,
    }), true));
    what.push(seg(t('dashboard.plainSummary.vsPeriod', { period: comparisonLabel })));
  }

  // ── Why it matters (one short sentence) ─────────────────────────
  let why = null;

  if (showCostPerUnit) {
    if (costPerUnitPct < 0) {
      why = [seg(t('dashboard.plainSummary.whyLowerCostPerUnit'))];
    } else {
      why = [seg(t('dashboard.plainSummary.whyHigherCostPerUnit'))];
    }
  } else if (hasCostData && hasPrev && costPct != null && Math.abs(costPct) >= 5) {
    if (costPct > 0) {
      why = [seg(t('dashboard.plainSummary.whyRisingLabourCost'))];
    } else {
      why = [seg(t('dashboard.plainSummary.whyLowerLabourCost'))];
    }
  } else if (stats.productivityRatio != null && stats.productivityRatio < 65 && taskCount >= 5) {
    why = [
      seg(t('dashboard.plainSummary.onlyCosted')),
      seg(`${stats.productivityRatio}%`, true),
      seg(t('dashboard.plainSummary.whyProductivityLowSuffix')),
    ];
  } else if (stats.productivityRatio != null && stats.productivityRatio >= 82 && taskCount >= 5) {
    why = [
      seg(`${stats.productivityRatio}%`, true),
      seg(t('dashboard.plainSummary.whyProductivityHighSuffix')),
    ];
  } else if (stats.totalDowntime > 0 && stats.totalDuration > 0) {
    const pct = Math.round((stats.totalDowntime / stats.totalDuration) * 100);
    if (pct >= 10) {
      why = [
        seg(`${pct}%`, true),
        seg(t('dashboard.plainSummary.whyDowntimeSuffix')),
      ];
    }
  } else if (hasCostData && curr.avgRate != null) {
    why = [
      seg(t('dashboard.plainSummary.blendedRate')),
      seg(`${formatCurrency(curr.avgRate, currency)}${t('dashboard.plainSummary.perHour')}`, true),
      seg(t('dashboard.plainSummary.whyBlendedRateSuffix')),
    ];
  } else if (stats.totalQty > 0 && stats.unitsPerHour != null) {
    why = [
      seg(t('dashboard.plainSummary.throughput')),
      seg(t('dashboard.plainSummary.unitsPerHour', { value: stats.unitsPerHour.toFixed(1) }), true),
      seg(t('dashboard.plainSummary.whyThroughputSuffix')),
    ];
  } else if (hasPrev && hoursPct != null) {
    if (hoursPct > 10) {
      why = [seg(t('dashboard.plainSummary.whyMoreHours'))];
    } else if (hoursPct < -10) {
      why = [seg(t('dashboard.plainSummary.whyFewerHours'))];
    }
  }

  if (!why) {
    why = [seg(t('dashboard.plainSummary.whyDefault'))];
  }

  // ── Recommended action (one practical sentence, only when data is strong enough) ──
  let action = null;

  if (taskCount >= 5) {
    const downtimePct =
      stats.totalDuration > 0
        ? Math.round((stats.totalDowntime / stats.totalDuration) * 100)
        : 0;

    if (showCostPerUnit && costPerUnitPct > 5) {
      action = t('dashboard.plainSummary.actionCostPerUnitUp');
    } else if (showCostPerUnit && costPerUnitPct < -5) {
      action = t('dashboard.plainSummary.actionCostPerUnitDown');
    } else if (hasCostData && hasPrev && costPct != null && costPct > 10) {
      action = t('dashboard.plainSummary.actionRisingCost');
    } else if (stats.productivityRatio != null && stats.productivityRatio < 65) {
      action = t('dashboard.plainSummary.actionLowProductivity');
    } else if (downtimePct >= 15) {
      action = t('dashboard.plainSummary.actionDowntime');
    } else if (hasPrev && tasksPct != null && tasksPct < -10) {
      action = t('dashboard.plainSummary.actionVolumeDrop', { period: comparisonLabel });
    } else if (hasPrev && tasksPct != null && tasksPct > 15) {
      action = t('dashboard.plainSummary.actionVolumeUp');
    } else if (stats.needsRateCount >= 3) {
      action = t('dashboard.plainSummary.actionMissingRates');
    } else if (hasCostData) {
      action = t('dashboard.plainSummary.actionKeepMonitoring');
    } else {
      action = t('dashboard.plainSummary.actionAddRates');
    }
  }

  return { title, what, why, action, taskCount };
}

function PlainSummaryCard({ summary }) {
  const { t } = useTranslation();
  if (!summary) return null;

  const renderSegs = (segs) =>
    segs.map((s, i) =>
      s.bold ? <strong key={i}>{s.text}</strong> : <span key={i}>{s.text}</span>
    );

  return (
    <div className="plain-summary-card">
      <div className="plain-summary-header">
        <FileText size={14} className="plain-summary-icon" aria-hidden="true" />
        <span className="plain-summary-title">{summary.title}</span>
      </div>
      <div className="plain-summary-body">
        <div className="plain-summary-section">
          <span className="plain-summary-section-label">{t('dashboard.whatHappened')}</span>
          <p className="plain-summary-p">{renderSegs(summary.what)}</p>
        </div>
        {summary.why && (
          <div className="plain-summary-section">
            <span className="plain-summary-section-label">{t('dashboard.whyItMatters')}</span>
            <p className="plain-summary-p">{renderSegs(summary.why)}</p>
          </div>
        )}
        {summary.action && (
          <div className="plain-summary-section plain-summary-action-section">
            <span className="plain-summary-section-label">{t('dashboard.recommendedAction')}</span>
            <p className="plain-summary-p plain-summary-action-text">{summary.action}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const relTime = makeRelTime(t);
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_ANALYTICS_FILTERS }));
  const [activeView, setActiveView] = useState(null);
  const [trendMode, setTrendMode] = useState('hours');
  const restoredViewRef = useRef(false);

  const toast = useToast();
  const { companyId } = useAuth();
  const { adminSettings } = useAdminSettings();
  const datePreset = filters.datePreset;
  const dateRange = useMemo(() => getDateRange(datePreset), [datePreset]);
  const comparisonRange = useMemo(() => getComparisonRange(datePreset), [datePreset]);
  const { calculating, lastRun, lastUpdatedCount, runCalculation } = useLabourCostCalculation();
  const { tasks: activeTasks, loading: loadingActive } = useAdminActiveTasks();
  const { tasks: completedTasks, loading: loadingCompleted } = useAdminRecentTasks(dateRange);
  const { tasks: prevTasks } = useAdminRecentTasks(comparisonRange);
  const { views: savedViews, loading: viewsLoading, saveView, renameView, deleteView, touchLastUsed } = useAnalyticsViews();
  const { areas: rooms } = useProductionAreas();
  const { categories } = useAllTaskCategories();
  const { tags } = useAllTaskTags();

  const currency = adminSettings?.labourCostCurrency || 'CAD';
  const defaultTeamRate = adminSettings?.defaultTeamHourlyRate ?? null;

  const { rates: workerRates } = useFloorWorkerRates();
  const workerRateMap = useMemo(() => {
    const m = {};
    for (const [id, rateData] of Object.entries(workerRates)) {
      if (rateData.hourlyRate != null) m[id] = rateData.hourlyRate;
    }
    return m;
  }, [workerRates]);

  const activeWorkerCount = useMemo(() => {
    const names = new Set();
    let anonymousCount = 0;
    for (const task of activeTasks) {
      if (task.employeeNames?.length) {
        task.employeeNames.forEach((n) => names.add(n));
      } else {
        anonymousCount += task.teamSize || 1;
      }
    }
    return names.size + anonymousCount;
  }, [activeTasks]);

  const stuckActiveCount = useMemo(() => {
    const now = Date.now();
    return activeTasks.filter((t) => {
      const startMs = t.startTime?.toDate?.().getTime() ?? null;
      if (!startMs) return false;
      const pausedMs = (t.pausedMinutes ?? 0) * 60000;
      return (now - startMs - pausedMs) > 8 * 3600 * 1000;
    }).length;
  }, [activeTasks]);

  const [totalLiveCost, setTotalLiveCost] = useState(null);
  useEffect(() => {
    if (!activeTasks.length) { setTotalLiveCost(null); return; }
    const compute = () => {
      const now = Date.now();
      let total = 0;
      let hasCost = false;
      for (const task of activeTasks) {
        const startMs = task.startTime?.toDate?.().getTime() ?? null;
        if (!startMs) continue;
        const isPaused = !!task.pausedAt;
        const pausedSeconds = (task.pausedMinutes ?? 0) * 60;
        let netElapsedSeconds;
        if (isPaused && task.pausedAt) {
          const pausedAtMs = task.pausedAt.toDate
            ? task.pausedAt.toDate().getTime()
            : new Date(task.pausedAt).getTime();
          netElapsedSeconds = Math.max(0, (pausedAtMs - startMs) / 1000 - pausedSeconds);
        } else {
          netElapsedSeconds = Math.max(0, (now - startMs) / 1000 - pausedSeconds);
        }
        const { cost } = calcLabourCost(
          netElapsedSeconds / 60,
          task.employeeIds,
          task.teamSize,
          workerRateMap,
          defaultTeamRate,
        );
        if (cost != null) { total += cost; hasCost = true; }
      }
      setTotalLiveCost(hasCost ? parseFloat(total.toFixed(2)) : null);
    };
    compute();
    const timer = setInterval(compute, 10000);
    return () => clearInterval(timer);
  }, [activeTasks, workerRateMap, defaultTeamRate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate missing labour costs once companyId is available
  useEffect(() => { runCalculation(); }, [runCalculation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toast feedback after manual recalc (lastUpdatedCount only set by explicit runs)
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (lastRun == null) return;
    if (isFirstRun.current) { isFirstRun.current = false; return; }
    if (lastUpdatedCount === 0) {
      toast(t('toast.labourCostsCurrent'), 'info');
    } else if (lastUpdatedCount != null) {
      toast(t('toast.labourCostsUpdated', { count: lastUpdatedCount }), 'success');
    }
  }, [lastRun]); // eslint-disable-line react-hooks/exhaustive-deps

  const { benchmarks } = useBestRunBenchmarks();
  const { notes: companyNotes } = useCompanyTaskNotes();

  const issueNoteTaskIds = useMemo(() => {
    const issueCats = new Set([
      'rework-required', 'material-delay', 'equipment-issue',
      'quality-concern', 'setup-longer', 'staffing-issue',
    ]);
    return new Set(companyNotes.filter((n) => issueCats.has(n.category)).map((n) => n.taskId));
  }, [companyNotes]);

  const baseTasks = filters.statusScope === 'active' ? activeTasks : completedTasks;

  const filteredTasks = useMemo(() => applyAnalyticsFilters(baseTasks, filters, {
    benchmarks,
    issueNoteTaskIds,
    dateRange: filters.statusScope === 'active' ? dateRange : undefined,
  }), [baseTasks, filters, benchmarks, issueNoteTaskIds, dateRange]);

  const filteredPrevTasks = useMemo(() => {
    if (filters.statusScope === 'active') return [];
    return applyAnalyticsFilters(prevTasks, filters, { benchmarks, issueNoteTaskIds });
  }, [prevTasks, filters, benchmarks, issueNoteTaskIds, filters.statusScope]);

  const workerOptions = useMemo(() => {
    const names = new Set();
    for (const task of [...completedTasks, ...activeTasks]) {
      for (const n of task.employeeNames || []) names.add(n);
    }
    return [...names].sort();
  }, [completedTasks, activeTasks]);

  const handleFiltersChange = (next) => {
    setFilters(validateAnalyticsFilters(next));
    setActiveView(null);
  };

  const handleApplyPreset = (presetKey, savedView) => {
    if (presetKey.startsWith('saved:')) {
      const view = savedView || savedViews.find((v) => v.id === presetKey.slice(6));
      if (!view) return;
      setFilters(validateAnalyticsFilters(view.filters));
      setActiveView({ type: 'saved', id: view.id, name: view.name });
      touchLastUsed(view.id);
      if (companyId) {
        try { localStorage.setItem(lastViewStorageKey(companyId), view.id); } catch { /* ignore */ }
      }
      return;
    }
    const preset = getPresetById(presetKey);
    if (!preset) return;
    setFilters(buildPresetFilters(presetKey));
    setActiveView({ type: 'preset', id: presetKey, label: t(preset.labelKey) });
    if (companyId) {
      try { localStorage.removeItem(lastViewStorageKey(companyId)); } catch { /* ignore */ }
    }
  };

  const handleResetFilters = () => {
    setFilters({ ...DEFAULT_ANALYTICS_FILTERS });
    setActiveView(null);
    if (companyId) {
      try { localStorage.removeItem(lastViewStorageKey(companyId)); } catch { /* ignore */ }
    }
  };

  const handleSaveView = async (name) => {
    try {
      await saveView(name, filters);
      toast(t('analyticsViews.savedSuccess'), 'success');
    } catch (err) {
      if (err?.code === 'DUPLICATE_NAME') {
        toast(t('analyticsViews.duplicateName'), 'error');
      } else {
        toast(t('analyticsViews.savedError'), 'error');
      }
      throw err;
    }
  };

  const handleRenameView = async (id, name) => {
    try {
      await renameView(id, name);
      if (activeView?.type === 'saved' && activeView.id === id) {
        setActiveView({ ...activeView, name });
      }
      toast(t('analyticsViews.renamedSuccess'), 'success');
    } catch (err) {
      if (err?.code === 'DUPLICATE_NAME') {
        toast(t('analyticsViews.duplicateName'), 'error');
      } else {
        toast(t('analyticsViews.savedError'), 'error');
      }
      throw err;
    }
  };

  const handleDeleteView = async (id) => {
    try {
      await deleteView(id);
      toast(t('analyticsViews.deletedSuccess'), 'success');
    } catch {
      toast(t('analyticsViews.savedError'), 'error');
    }
  };

  // ── KPI + chart data ──────────────────────────────────────────

  const stats = useMemo(() => {
    const totalDuration = filteredTasks.reduce((s, t) => s + (t.durationMinutes || 0), 0);
    const totalLabourHours = filteredTasks.reduce((s, t) => s + (t.labourHours || 0), 0);
    const withCost = filteredTasks.filter((t) => t.labourCost != null);
    const totalLabourCost = withCost.reduce((s, t) => s + t.labourCost, 0);
    const totalQty = filteredTasks.reduce((s, t) => s + (t.quantityCompleted || 0), 0);
    // Units belonging only to costed tasks — the correct denominator for
    // cost-per-unit. Using totalQty (which includes uncosted tasks' units) would
    // understate cost/unit by crediting units whose cost isn't in the numerator.
    const costedQty = withCost.reduce((s, t) => s + (t.quantityCompleted || 0), 0);
    const totalDowntime = filteredTasks.reduce((s, t) => s + (t.downtimeMinutes || 0), 0);
    const avgDuration = filteredTasks.length ? totalDuration / filteredTasks.length : null;
    const avgCost = withCost.length ? totalLabourCost / withCost.length : null;
    const needsRateCount = filteredTasks.filter((t) => t.labourCost == null).length;

    // Throughput metrics
    const totalDurationHrs = totalDuration / 60;
    const unitsPerHour = totalQty > 0 && totalDurationHrs > 0 ? totalQty / totalDurationHrs : null;
    const unitsPerLabourHour = totalQty > 0 && totalLabourHours > 0 ? totalQty / totalLabourHours : null;
    const avgCostPerUnit = totalLabourCost > 0 && costedQty > 0 ? totalLabourCost / costedQty : null;

    // Charts
    const withCostTasks = filteredTasks.filter((t) => t.labourCost != null && t.labourCost > 0);
    const topCategories = groupByDuration(filteredTasks, (task) => task.taskCategoryName, t);
    const topCostCategories = groupByCost(withCostTasks, (task) => task.taskCategoryName, t);
    const costByRoom = groupByCost(withCostTasks, (task) => task.roomName, t);

    // Employee cost distribution (split equally across assigned)
    const empMap = {};
    for (const task of withCostTasks) {
      const names = task.employeeNames?.length ? task.employeeNames : [t('dashboard.teamX', { size: task.teamSize || '?' })];
      const share = task.labourCost / names.length;
      for (const name of names) empMap[name] = (empMap[name] || 0) + share;
    }
    const costByEmployee = Object.entries(empMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 7)
      .map(([name, cost]) => ({ name: trunc(name, t, 18), cost }));

    // Production vs support vs delay duration
    const typeMap = { production: 0, support: 0, delay: 0, other: 0 };
    for (const t of filteredTasks) {
      const k = t.taskType in typeMap ? t.taskType : 'other';
      typeMap[k] += t.durationMinutes || 0;
    }
    const totalTypeMinutes = Object.values(typeMap).reduce((s, v) => s + v, 0);
    const productivityRatio = totalTypeMinutes > 0
      ? parseFloat(((typeMap.production / totalTypeMinutes) * 100).toFixed(0))
      : null;
    const typeBreakdown = Object.entries(typeMap)
      .filter(([, v]) => v > 0)
      .map(([type, minutes]) => ({ type, minutes }));

    // Top-cost tasks — intentionally capped at 3 for the summary panel
    const topCostTasks = [...withCostTasks]
      .sort((a, b) => b.labourCost - a.labourCost)
      .slice(0, 3);

    // Recent activity: tasks already ordered desc by submittedAt from hook
    const recentActivity = filteredTasks.slice(0, 6);

    // ── Trend: task volume over time ──────────────────────────
    const daySpan = Math.ceil((dateRange.end - dateRange.start) / 86400000);
    const trendGranularity = daySpan <= 1 ? null : daySpan <= 35 ? 'day' : daySpan <= 120 ? 'week' : 'month';
    let trendData = [];
    if (trendGranularity && filteredTasks.length > 0) {
      const buckets = {};
      for (const t of filteredTasks) {
        const ts = t.submittedAt?.toDate?.() ?? (t.submittedAt ? new Date(t.submittedAt) : null);
        if (!ts) continue;
        let key, label;
        if (trendGranularity === 'day') {
          key = format(ts, 'yyyy-MM-dd');
          label = daySpan <= 7 ? format(ts, 'EEE') : format(ts, 'MMM d');
        } else if (trendGranularity === 'week') {
          const ws = startOfWeek(ts, { weekStartsOn: 1 });
          key = format(ws, 'yyyy-MM-dd');
          label = format(ws, 'MMM d');
        } else {
          key = format(ts, 'yyyy-MM');
          label = format(ts, 'MMM yyyy');
        }
        if (!buckets[key]) buckets[key] = { label, count: 0, minutes: 0, cost: 0, units: 0 };
        buckets[key].count++;
        buckets[key].minutes += t.durationMinutes || 0;
        if (t.labourCost != null) buckets[key].cost += t.labourCost;
        if (t.quantityCompleted != null) buckets[key].units += t.quantityCompleted;
      }
      trendData = Object.entries(buckets)
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([, v]) => ({
          ...v,
          hours: parseFloat((v.minutes / 60).toFixed(1)),
          costPerUnit: v.cost > 0 && v.units > 0 ? parseFloat((v.cost / v.units).toFixed(4)) : null,
        }));
    }

    // ── Team performance (individual employee name records) ───
    const teamPerfMap = {};
    for (const t of filteredTasks) {
      if (!t.employeeNames?.length) continue;
      for (const name of t.employeeNames) {
        if (!teamPerfMap[name]) teamPerfMap[name] = { tasks: 0, totalMinutes: 0, totalCost: 0 };
        teamPerfMap[name].tasks++;
        teamPerfMap[name].totalMinutes += t.durationMinutes || 0;
        if (t.labourCost != null) {
          teamPerfMap[name].totalCost += t.labourCost / t.employeeNames.length;
        }
      }
    }
    const teamSummary = Object.entries(teamPerfMap)
      .sort((a, b) => (b[1].totalCost - a[1].totalCost) || (b[1].tasks - a[1].tasks))
      .slice(0, 12)
      .map(([name, d]) => ({
        name: trunc(name, t, 26),
        tasks: d.tasks,
        totalMinutes: d.totalMinutes,
        avgMinutes: d.tasks ? Math.round(d.totalMinutes / d.tasks) : 0,
        totalCost: d.totalCost > 0 ? parseFloat(d.totalCost.toFixed(2)) : null,
        avgCostPerTask: d.tasks && d.totalCost > 0 ? parseFloat((d.totalCost / d.tasks).toFixed(2)) : null,
      }));

    // ── Exceptions / data issues ──────────────────────────────
    const longTaskCount = filteredTasks.filter((t) => (t.durationMinutes || 0) > 480).length;
    const missingEndTimeCount = filteredTasks.filter((t) => !t.endTime).length;

        return {
      totalDuration, totalLabourHours, totalLabourCost, avgCost, avgDuration,
      totalQty, totalDowntime, needsRateCount,
      unitsPerHour, unitsPerLabourHour, avgCostPerUnit,
      topCategories, topCostCategories, costByRoom, costByEmployee, typeBreakdown,
      topCostTasks, recentActivity,
      withCostCount: withCost.length,
      trendData, trendGranularity, teamSummary, longTaskCount, missingEndTimeCount, productivityRatio,
    };
  }, [filteredTasks, dateRange, currency, t]);

  // ── Period-over-period comparison ─────────────────────────────
  const comparison = useMemo(() => {
    const curr = periodSummary(filteredTasks);
    const prev = periodSummary(filteredPrevTasks);
    return {
      curr,
      prev,
      hasPrev: filteredPrevTasks.length > 0,
      costPct: pctChange(curr.labourCost, prev.labourCost),
      hoursPct: pctChange(curr.labourHours, prev.labourHours),
      tasksPct: pctChange(curr.taskCount, prev.taskCount),
      workerPct: pctChange(curr.workerCount, prev.workerCount),
      avgRatePct: pctChange(curr.avgRate, prev.avgRate),
      costPerUnitPct: pctChange(curr.costPerUnit, prev.costPerUnit),
      avgDurationPct: pctChange(curr.avgDuration, prev.avgDuration),
    };
  }, [filteredTasks, filteredPrevTasks]);

  const comparisonLabel = t(`common.${getComparisonLabelKey(datePreset)}`);

  const insights = useMemo(() => generateInsights({
    stats,
    comparison,
    completedTasks: filteredTasks,
    comparisonLabel,
    currency,
    t,
  }), [stats, comparison, filteredTasks, comparisonLabel, currency, t]);

  const plainSummary = useMemo(() => generatePlainSummary({
    datePreset,
    completedTasks: filteredTasks,
    stats,
    comparison,
    comparisonLabel,
    currency,
    tagFilterName: filters.tagId ? tags.find((tg) => tg.id === filters.tagId)?.name : null,
    t,
  }), [datePreset, filteredTasks, stats, comparison, comparisonLabel, currency, filters.tagId, tags, t]);

  // Top categories (by task count) from the current period that have a
  // valid all-time benchmark. Cap at 3 to keep the section compact.
  const benchmarkCategories = useMemo(() => {
    const catCount = {};
    for (const task of filteredTasks) {
      const key = getBenchmarkKey(task);
      const label =
        task.taskCategoryName ||
        (task.isCustomTaskName ? task.customTaskName : task.taskName) ||
        key;
      if (!catCount[key]) catCount[key] = { key, label, count: 0 };
      catCount[key].count++;
    }
    return Object.values(catCount)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .filter(({ key }) => benchmarks.get(key) != null);
  }, [filteredTasks, benchmarks]);

  const { bottlenecks, hasEnoughData: bottlenecksHasData } = useMemo(
    () => detectBottlenecks(filteredTasks, { notes: companyNotes, currency }),
    [filteredTasks, companyNotes, currency, i18n.language],
  );

  const tableRows = filteredTasks;
  const tableLoading = filters.statusScope === 'active' ? loadingActive : loadingCompleted;
  const filtersActive = hasActiveFilters(filters);
  const staleFilterHints = useMemo(
    () => getStaleFilterHints(filters, { rooms, categories, tags, workers: workerOptions }),
    [filters, rooms, categories, tags, workerOptions],
  );

  // Restore last-used saved view after saved views load (once per session).
  useEffect(() => {
    if (restoredViewRef.current || viewsLoading || !companyId) return;
    restoredViewRef.current = true;
    let lastId;
    try { lastId = localStorage.getItem(lastViewStorageKey(companyId)); } catch { return; }
    if (!lastId) return;
    const view = savedViews.find((v) => v.id === lastId);
    if (!view) {
      try { localStorage.removeItem(lastViewStorageKey(companyId)); } catch { /* ignore */ }
      return;
    }
    setFilters(validateAnalyticsFilters(view.filters));
    setActiveView({ type: 'saved', id: view.id, name: view.name });
  }, [viewsLoading, savedViews, companyId]);

  const handleSort = (key) => {
    const next = { ...filters };
    if (filters.sortKey === key) {
      next.sortDir = filters.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      next.sortKey = key;
      next.sortDir = 'desc';
    }
    setFilters(validateAnalyticsFilters(next));
  };

  const thProps = { sortKey: filters.sortKey, sortDir: filters.sortDir, onSort: handleSort };
  const fmt1 = (n) => (n != null ? n.toFixed(1) : '—');

  const hasExceptions = stats.longTaskCount > 0 || stats.missingEndTimeCount > 0 || stuckActiveCount > 0;
  const trendHasCost = stats.trendData.some((d) => d.cost > 0);
  const trendHasCostPerUnit = stats.trendData.filter((d) => d.costPerUnit != null).length >= 2;
  const trendLabel =
    stats.trendGranularity === 'week' ? t('admin.byWeek') :
    stats.trendGranularity === 'month' ? t('admin.byMonth') :
    t('admin.byDay');

  // ── JSX ─────────────────────────────────────────────────────

  return (
    <div className="dash-page">

      {/* ══ Sticky header ══ */}
      <div className="dash-header">
        {/* Zone 1 — Identity */}
        <div className="dash-header-identity">
          <h1 className="dash-title">{t('admin.dashboard')}</h1>
          <span className="dash-date">{formatDate(new Date())}</span>
          {lastRun && <span className="dash-lastrun">{t('admin.costsUpdated')} {relTime(lastRun)}</span>}
        </div>

        {/* Zone 2 — Date range (primary control) */}
        <div className="date-presets">
          {DATE_PRESETS_SHORT.map((p) => (
            <button
              key={p}
              className={`btn btn-sm ${datePreset === p ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => handleFiltersChange({ ...filters, datePreset: p })}
            >
              {t(`common.${p}`)}
            </button>
          ))}
          <span className="date-presets-divider" />
          {DATE_PRESETS_LONG.map((p) => (
            <button
              key={p}
              className={`btn btn-sm ${datePreset === p ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => handleFiltersChange({ ...filters, datePreset: p })}
            >
              {t(`common.${p}`)}
            </button>
          ))}
        </div>

        {/* Zone 3 — Actions */}
        <div className="dash-header-actions">
          {stats.needsRateCount > 0 && filters.statusScope === 'completed' && (
            <span className="pending-badge">{completedTasks.filter((t) => t.labourCost == null).length} {t('admin.needsRate')}</span>
          )}
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => runCalculation({ force: true })}
            disabled={calculating}
            title={t('dashboard.recalcTitle')}
          >
            <RefreshCw size={14} className={calculating ? 'spin-icon' : ''} />
            {calculating ? t('admin.calculating') : t('admin.recalcCosts')}
          </button>
          <ExportButton
            options={[
              {
                id: 'tasks',
                label: t('dashboard.exportTasksCsv'),
                description: filteredTasks.length > 0
                  ? `${t('productionMeta.taskCount', { count: filteredTasks.length })} · ${t(`common.${datePreset}`)}`
                  : t('dashboard.noTasksInView'),
                disabled: filteredTasks.length === 0,
                successMessage: t('tasksPage.exportSuccess', { count: filteredTasks.length }),
                onExport: () => {
                  const csv = buildProductionTasksCSV(filteredTasks, currency);
                  downloadCSV(makeExportFilename('tasks', dateRange), csv);
                },
              },
              {
                id: 'workers',
                label: t('dashboard.exportWorkerCsv'),
                description: filteredTasks.length > 0
                  ? `${t('dashboard.workerCount', { count: stats.teamSummary.length })} · ${t(`common.${datePreset}`)}`
                  : t('dashboard.noDataInView'),
                disabled: filteredTasks.length === 0,
                successMessage: t('dashboard.exportWorkerSuccess'),
                onExport: () => {
                  const csv = buildWorkerPerformanceCSV(filteredTasks, currency);
                  downloadCSV(makeExportFilename('workers', dateRange), csv);
                },
              },
            ]}
          />
        </div>
      </div>

      <AnalyticsViewsPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onApplyPreset={handleApplyPreset}
        onReset={handleResetFilters}
        activeView={activeView}
        filtersActive={filtersActive}
        savedViews={savedViews}
        viewsLoading={viewsLoading}
        onSaveView={handleSaveView}
        onRenameView={handleRenameView}
        onDeleteView={handleDeleteView}
        workers={workerOptions}
        rooms={rooms}
        categories={categories}
        tags={tags.filter((tg) => tg.isActive)}
        staleFilterHints={staleFilterHints}
        resultCount={filteredTasks.length}
        baseCount={baseTasks.length}
      />

      <div className="dash-body">

        {/* ══ Live Production panel ══ */}
        {activeTasks.length > 0 && (
          <div className="live-panel">
            <div className="live-panel-header">
              <div className="live-panel-left">
                <span className="live-indicator" />
                <span className="live-panel-title">{t('dashboard.liveProduction')}</span>
                <span className="badge badge-active">{t('productionMeta.taskCount', { count: activeTasks.length })}</span>
              </div>
              <div className="live-panel-stats">
                <div className="live-stat">
                  <span className="live-stat-label">{t('dashboard.activeWorkers')}</span>
                  <span className="live-stat-value">{activeWorkerCount}</span>
                </div>
                {totalLiveCost != null && (
                  <>
                    <span className="live-stat-divider" />
                    <div className="live-stat">
                      <span className="live-stat-label">{t('dashboard.runningCost')}</span>
                      <span className="live-stat-value live-stat-cost">~{formatCurrency(totalLiveCost, currency)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('dashboard.task')}</th>
                    <th>{t('dashboard.type')}</th>
                    <th>{t('dashboard.status')}</th>
                    <th>{t('dashboard.workers')}</th>
                    <th>{t('dashboard.room')}</th>
                    <th>{t('dashboard.started')}</th>
                    <th>{t('dashboard.elapsed')}</th>
                    <th className="text-right">{t('dashboard.liveCost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {activeTasks.map((task) => (
                    <ActiveRow
                      key={task.id}
                      task={task}
                      workerRateMap={workerRateMap}
                      defaultTeamRate={defaultTeamRate}
                      currency={currency}
                      t={t}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ KPI Row 1: Operations ══ */}
        <div className="kpi-grid">
          <div className="kpi-card kpi-accent-green">
            <span className="kpi-label">{t('admin.liveActive')}</span>
            <span className="kpi-value">{activeTasks.length}</span>
            <span className="kpi-sub">{t('admin.tasksInProgress')}</span>
          </div>
          <div className="kpi-card">
            <span className="kpi-label">{filters.statusScope === 'active' ? t('admin.activeTasks') : t('admin.completedTasks')}</span>
            <span className="kpi-value">{filteredTasks.length}</span>
            <span className="kpi-sub">
              {comparison.hasPrev
                ? <><Delta pct={comparison.tasksPct} /> {comparisonLabel}</>
                : t('admin.inSelectedPeriod')}
            </span>
          </div>
          <div className="kpi-card kpi-accent-amber">
            <span className="kpi-label">{t('dashboard.labourCost')}</span>
            <span className="kpi-value">
              {stats.withCostCount > 0 ? formatCurrency(stats.totalLabourCost, currency) : '—'}
            </span>
            <span className="kpi-sub">
              {comparison.hasPrev && comparison.prev.hasCost
                ? <><Delta pct={comparison.costPct} goodWhenDown /> {comparisonLabel}</>
                : stats.withCostCount > 0
                ? t('dashboard.costedCount', { costed: stats.withCostCount, total: filteredTasks.length })
                : filteredTasks.length === 0
                ? t('dashboard.noTasksPeriod')
                : t('dashboard.addRatesToTrack')}
            </span>
          </div>
          <div className="kpi-card">
            <span className="kpi-label">{t('dashboard.totalLabourHours')}</span>
            <span className="kpi-value">{fmt1(stats.totalLabourHours)}h</span>
            <span className="kpi-sub">
              {comparison.hasPrev
                ? <><Delta pct={comparison.hoursPct} /> {comparisonLabel}</>
                : t('admin.personHoursTotal')}
            </span>
          </div>
          {/* 5th card: Avg Cost/Unit when quantity data exists, otherwise Avg Cost/Task */}
          {stats.avgCostPerUnit != null ? (
            <div className="kpi-card kpi-accent-amber">
              <span className="kpi-label">{t('admin.avgCostPerUnit')}</span>
              <span className="kpi-value">{formatCurrency(stats.avgCostPerUnit, currency)}</span>
              <span className="kpi-sub">
                {comparison.hasPrev && comparison.costPerUnitPct != null
                  ? <><Delta pct={comparison.costPerUnitPct} goodWhenDown /> {comparisonLabel}</>
                  : t('admin.labourCostSub')}
              </span>
            </div>
          ) : (
            <div className="kpi-card">
              <span className="kpi-label">{t('admin.avgCostPerTask')}</span>
              <span className="kpi-value">
                {stats.avgCost != null ? formatCurrency(stats.avgCost, currency) : (
                  <span className="kpi-empty-state">{t('dashboard.addRatesToCalculate')}</span>
                )}
              </span>
              <span className="kpi-sub">{t('dashboard.perCostedTask')}</span>
            </div>
          )}
        </div>

        {/* ══ KPI Row 2: Throughput — only shown when quantity data exists ══ */}
        {stats.totalQty > 0 && (
          <div className="kpi-grid">
            <div className="kpi-card">
              <span className="kpi-label">{t('admin.totalUnits')}</span>
              <span className="kpi-value">{stats.totalQty.toLocaleString()}</span>
              <span className="kpi-sub">{t('admin.produced')}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">{t('admin.unitsPerHourCard')}</span>
              <span className="kpi-value">{fmt1(stats.unitsPerHour)}</span>
              <span className="kpi-sub">{t('admin.clockTimeThroughput')}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">{t('dashboard.unitsPerLabourHour')}</span>
              <span className="kpi-value">{fmt1(stats.unitsPerLabourHour)}</span>
              <span className="kpi-sub">{t('admin.perPersonHour')}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">{t('dashboard.avgTaskDuration')}</span>
              <span className="kpi-value">
                {stats.avgDuration != null ? formatDuration(Math.round(stats.avgDuration)) : '—'}
              </span>
              <span className="kpi-sub">
                {comparison.hasPrev && comparison.avgDurationPct != null
                  ? <><Delta pct={comparison.avgDurationPct} goodWhenDown /> {comparisonLabel}</>
                  : t('dashboard.perTask')}
              </span>
            </div>
          </div>
        )}

        {/* ══ Period summary — context strip ══ */}
        {filteredTasks.length > 0 && (
          <div className="period-summary">
            <div className="period-metrics">
              <div className="period-metric">
                <span className="pm-label">{t('dashboard.workersActive')}</span>
                <span className="pm-value">{comparison.curr.workerCount || '—'}</span>
              </div>
              {comparison.curr.avgRate != null && (
                <>
                  <div className="pm-divider" />
                  <div className="period-metric">
                    <span className="pm-label">{t('dashboard.avgRate')}</span>
                    <span className="pm-value">{formatCurrency(comparison.curr.avgRate, currency)}/hr</span>
                  </div>
                </>
              )}
              {stats.avgDuration != null && (
                <>
                  <div className="pm-divider" />
                  <div className="period-metric">
                    <span className="pm-label">{t('dashboard.avgTaskDuration')}</span>
                    <span className="pm-value">{formatDuration(Math.round(stats.avgDuration))}</span>
                  </div>
                </>
              )}
              {comparison.curr.totalUnits > 0 && (
                <>
                  <div className="pm-divider" />
                  <div className="period-metric">
                    <span className="pm-label">{t('dashboard.totalUnits')}</span>
                    <span className="pm-value">{comparison.curr.totalUnits.toLocaleString()}</span>
                  </div>
                </>
              )}
            </div>
            {comparison.hasPrev && (
              <div className="period-deltas">
                <span className="pd-label">{t('dashboard.vsLabel', { label: comparisonLabel })}</span>
                <div className="pd-chips">
                  {comparison.tasksPct != null && (
                    <span className="pd-chip">{t('dashboard.chipTasks')} <Delta pct={comparison.tasksPct} /></span>
                  )}
                  {comparison.prev.hasCost && comparison.costPct != null && (
                    <span className="pd-chip">{t('dashboard.chipCost')} <Delta pct={comparison.costPct} goodWhenDown /></span>
                  )}
                  {comparison.hoursPct != null && (
                    <span className="pd-chip">{t('dashboard.chipHours')} <Delta pct={comparison.hoursPct} /></span>
                  )}
                  {comparison.avgDurationPct != null && (
                    <span className="pd-chip">{t('dashboard.chipAvgDuration')} <Delta pct={comparison.avgDurationPct} goodWhenDown /></span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ Plain-language summary ══ */}
        {plainSummary && <PlainSummaryCard summary={plainSummary} />}

        {/* ══ Insight cards ══ */}
        {insights.length > 0 && (
          <div className="insight-cards-section">
            <div className="insight-cards-label">
              <Lightbulb size={11} />
              {t('dashboard.insightCount', { count: insights.length })}
            </div>
            <div className="insight-cards-grid">
              {insights.map((insight) => (
                <InsightCard key={insight.id} insight={insight} />
              ))}
            </div>
          </div>
        )}

        {/* ══ Bottleneck detection ══ */}
        {filteredTasks.length > 0 && (
          <BottleneckCards bottlenecks={bottlenecks} hasEnoughData={bottlenecksHasData} />
        )}

        {/* ══ Best Run Benchmarks ══ */}
        {benchmarkCategories.length > 0 && (
          <div className="bm-section">
            <div className="bm-section-header">
              <Trophy size={11} className="bm-section-icon" aria-hidden="true" />
              {t('dashboard.bestRunBenchmarks')}
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>
                {t('dashboard.allTimeBest')}
              </span>
            </div>
            <div className="bm-grid">
              {benchmarkCategories.map(({ key, label }) => (
                <BenchmarkCard
                  key={key}
                  benchmark={benchmarks.get(key)}
                  mode="admin"
                  currency={currency}
                  title={label}
                />
              ))}
            </div>
          </div>
        )}

        {/* ══ Exceptions / data issues ══ */}
        {hasExceptions && (
          <div className="exceptions-row">
            <AlertTriangle size={13} className="exc-icon" />
            <span className="exc-label">{t('admin.needsAttention')}</span>
            <span className="exc-divider" />
            {stats.longTaskCount > 0 && (
              <span className="exc-item exc-warn">
                {t('productionMeta.taskCount', { count: stats.longTaskCount })} {t('dashboard.ranOver8h')}
              </span>
            )}
            {stats.missingEndTimeCount > 0 && (
              <span className="exc-item exc-warn">
                {t('productionMeta.taskCount', { count: stats.missingEndTimeCount })} {t('dashboard.missingEndTime')}
              </span>
            )}
            {stuckActiveCount > 0 && (
              <span className="exc-item exc-warn">
                {t('productionMeta.taskCount', { count: stuckActiveCount })} {t('dashboard.runningOver8h')}
              </span>
            )}
          </div>
        )}

        {/* ══ Production trend chart ══ */}
        {stats.trendData.length > 1 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">{t('admin.productionTrend')}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                {trendHasCost && (
                  <div className="trend-toggle">
                    <button
                      className={`trend-toggle-btn${trendMode === 'hours' ? ' active' : ''}`}
                      onClick={() => setTrendMode('hours')}
                    >
                      {t('dashboard.hoursToggle')}
                    </button>
                    <button
                      className={`trend-toggle-btn${trendMode === 'cost' ? ' active' : ''}`}
                      onClick={() => setTrendMode('cost')}
                    >
                      {t('dashboard.labourCostToggle')}
                    </button>
                  </div>
                )}
                <span className="text-muted text-sm">{trendLabel} · {t('productionMeta.taskCount', { count: filteredTasks.length })}</span>
              </div>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.trendData} margin={{ left: 0, right: 20, top: 4, bottom: 4 }}>
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={trendMode === 'cost' ? (v) => formatCurrency(v, currency) : (v) => `${v}h`}
                    width={trendMode === 'cost' ? 64 : 34}
                  />
                  <Tooltip
                    content={trendMode === 'cost'
                      ? (props) => <CostTrendTooltip {...props} currency={currency} />
                      : <TrendTooltip />}
                    cursor={{ fill: 'var(--surface-2)' }}
                  />
                  {trendMode === 'cost'
                    ? <Bar dataKey="cost" fill="var(--color-warning)" radius={[3, 3, 0, 0]} maxBarSize={56} />
                    : <Bar dataKey="hours" fill="var(--color-brand)" radius={[3, 3, 0, 0]} maxBarSize={56} />
                  }
                </BarChart>
              </ResponsiveContainer>

              {trendHasCostPerUnit && (
                <div className="trend-cost-unit-section">
                  <div className="trend-cost-unit-label">{t('dashboard.costPerUnitLabel')} — {trendLabel}</div>
                  <ResponsiveContainer width="100%" height={90}>
                    <LineChart
                      data={stats.trendData.filter((d) => d.costPerUnit != null)}
                      margin={{ left: 0, right: 20, top: 4, bottom: 4 }}
                    >
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                        tickLine={false}
                        axisLine={false}
                        width={64}
                        tickFormatter={(v) => formatCurrency(v, currency)}
                      />
                      <Tooltip
                        formatter={(v) => [formatCurrency(v, currency), t('dashboard.costPerUnitLabel')]}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        cursor={{ stroke: 'var(--border-color)' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="costPerUnit"
                        stroke="var(--color-info)"
                        strokeWidth={2}
                        dot={{ r: 3, fill: 'var(--color-info)' }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ Main 2-col: charts + activity feed ══ */}
        {(stats.topCategories.length > 0 || stats.recentActivity.length > 0) && (
          <>
          <div className="dash-section-label">{t('dashboard.whereTimeWent')}</div>
          <div className="dash-main-grid">

            {/* Charts column */}
            <div className="dash-charts-col">
              {stats.topCategories.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">{t('admin.timeByCategory')}</span>
                    <span className="text-muted text-sm">{t('productionMeta.taskCount', { count: filteredTasks.length })} · {t('common.minutes')}</span>
                  </div>
                  <div className="chart-body">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={stats.topCategories}
                        layout="vertical"
                        margin={{ left: 4, right: 40, top: 4, bottom: 4 }}
                      >
                        <XAxis type="number" hide tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={170}
                          tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          formatter={(v) => [formatDuration(v), t('dashboard.durationLabel')]}
                          contentStyle={CHART_TOOLTIP_STYLE}
                          cursor={{ fill: 'var(--surface-2)' }}
                        />
                        <Bar dataKey="minutes" fill="var(--color-brand)" radius={[0, 4, 4, 0]} maxBarSize={20} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {stats.topCostCategories.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">{t('admin.labourCostByCategory')}</span>
                    <span className="text-muted text-sm">{currency}</span>
                  </div>
                  <div className="chart-body">
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={stats.topCostCategories}
                        layout="vertical"
                        margin={{ left: 4, right: 40, top: 4, bottom: 4 }}
                      >
                        <XAxis type="number" hide tickLine={false} axisLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={170}
                          tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          formatter={(v) => [formatCurrency(v, currency), t('dashboard.costLabel')]}
                          contentStyle={CHART_TOOLTIP_STYLE}
                          cursor={{ fill: 'var(--surface-2)' }}
                        />
                        <Bar dataKey="cost" fill="var(--color-warning)" radius={[0, 4, 4, 0]} maxBarSize={20} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Activity feed */}
            <div className="dash-feed-col">
              <div className="card feed-card">
                <div className="card-header">
                  <span className="card-title">
                    <span className="live-indicator" />
                    {t('dashboard.recentActivity')}
                  </span>
                  <span className="text-muted text-sm">
                    <Link
                      to={`/admin/production-replay?date=${format(new Date(), 'yyyy-MM-dd')}`}
                      className="dash-replay-link"
                    >
                      {t('nav.productionReplay')}
                    </Link>
                    {' · '}{t('dashboard.mostRecent', { count: stats.recentActivity.length })}
                  </span>
                </div>
                {stats.recentActivity.length === 0 ? (
                  <div className="empty-state" style={{ padding: 'var(--space-8) var(--space-4)' }}>
                    <p className="text-sm">{t('common.noData')}</p>
                  </div>
                ) : (
                  <div className="feed-list">
                    {stats.recentActivity.map((task) => (
                      <div key={task.id} className="feed-item">
                        <div className="feed-item-main">
                          <span className="feed-task-name">
                            {task.isCustomTaskName ? task.customTaskName : task.taskName}
                          </span>
                          <span className={`badge badge-${task.taskType}`} style={{ fontSize: 10 }}>
                            {task.taskType}
                          </span>
                        </div>
                        <div className="feed-item-meta">
                          <span className="text-sm text-muted">
                            {task.employeeNames?.join(', ') || t('dashboard.teamX', { size: task.teamSize })}
                            {task.roomName ? ` · ${task.roomName}` : ''}
                          </span>
                          {task.labourCost != null && (
                            <span className="feed-cost">{formatCurrency(task.labourCost, currency)}</span>
                          )}
                        </div>
                        <div className="feed-item-time">
                          <span>{formatDuration(task.durationMinutes)}</span>
                          {task.quantityCompleted != null && (
                            <span>· {task.quantityCompleted} {task.unitType || t('units.units')}</span>
                          )}
                          <span className="feed-rel-time">{relTime(task.submittedAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          </>
        )}

        {/* ══ Breakdown row: type / room / employee ══ */}
        {(stats.typeBreakdown.length > 0 || stats.costByRoom.length > 0 || stats.costByEmployee.length > 0) && (
          <div className="dash-breakdown-grid">
            {stats.typeBreakdown.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <span className="card-title">{t('admin.productionVsSupport')}</span>
                  <span className="text-muted text-sm">
                    {stats.productivityRatio != null
                      ? t('dashboard.directProductionPct', { pct: stats.productivityRatio })
                      : t('admin.timeSplit')}
                  </span>
                </div>
                <div className="chart-body">
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart
                      data={stats.typeBreakdown}
                      layout="vertical"
                      margin={{ left: 4, right: 28, top: 4, bottom: 4 }}
                    >
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="type"
                        width={80}
                        tick={{ fontSize: 12, fill: 'var(--text-secondary)', textTransform: 'capitalize' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        formatter={(v) => [formatDuration(v), t('dashboard.durationLabel')]}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        cursor={{ fill: 'var(--surface-2)' }}
                      />
                      <Bar dataKey="minutes" radius={[0, 4, 4, 0]} maxBarSize={22}>
                        {stats.typeBreakdown.map((entry) => (
                          <Cell key={entry.type} fill={TYPE_COLORS[entry.type] || '#9ca3af'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {stats.costByRoom.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <span className="card-title">{t('admin.costByRoom')}</span>
                  <span className="text-muted text-sm">{currency}</span>
                </div>
                <div className="chart-body">
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart
                      data={stats.costByRoom}
                      layout="vertical"
                      margin={{ left: 4, right: 28, top: 4, bottom: 4 }}
                    >
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={110}
                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        formatter={(v) => [formatCurrency(v, currency), t('dashboard.costLabel')]}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        cursor={{ fill: 'var(--surface-2)' }}
                      />
                      <Bar dataKey="cost" fill="var(--color-accent)" radius={[0, 4, 4, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {stats.costByEmployee.length > 0 && (
              <div className="card">
                <div className="card-header">
                  <span className="card-title">{t('admin.costByEmployee')}</span>
                  <span className="text-muted text-sm">{currency}</span>
                </div>
                <div className="chart-body">
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart
                      data={stats.costByEmployee}
                      layout="vertical"
                      margin={{ left: 4, right: 28, top: 4, bottom: 4 }}
                    >
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={110}
                        tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        formatter={(v) => [formatCurrency(v, currency), t('dashboard.costLabel')]}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        cursor={{ fill: 'var(--surface-2)' }}
                      />
                      <Bar dataKey="cost" fill="var(--color-brand-light)" radius={[0, 4, 4, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ Team performance ══ */}
        {stats.teamSummary.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">{t('admin.teamPerformance')}</span>
              <span className="text-muted text-sm">
                {t('dashboard.workerCount', { count: stats.teamSummary.length })} · {t(`common.${datePreset}`)}
              </span>
            </div>
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('dashboard.worker')}</th>
                    <th className="text-right">{t('dashboard.tasks')}</th>
                    <th className="text-right">{t('dashboard.totalTime')}</th>
                    <th className="text-right">{t('dashboard.avgDurationCol')}</th>
                    <th className="text-right">{t('dashboard.labourCostCol')}</th>
                    <th className="text-right">{t('dashboard.avgCostPerTaskCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.teamSummary.map((row) => (
                    <tr key={row.name}>
                      <td className="font-semibold text-sm">{row.name}</td>
                      <td className="text-right text-sm">{row.tasks}</td>
                      <td className="text-right text-sm">{formatDuration(row.totalMinutes)}</td>
                      <td className="text-right text-sm">{formatDuration(row.avgMinutes)}</td>
                      <td
                        className="text-right text-sm"
                        style={{ color: row.totalCost ? 'var(--color-warning)' : undefined, fontWeight: row.totalCost ? 600 : undefined }}
                      >
                        {row.totalCost != null ? formatCurrency(row.totalCost, currency) : '—'}
                      </td>
                      <td className="text-right text-sm">
                        {row.avgCostPerTask != null ? formatCurrency(row.avgCostPerTask, currency) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══ Top Cost Drivers (top 3 — compact) ══ */}
        {stats.topCostTasks.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span className="card-title">{t('dashboard.topCostDrivers')}</span>
            </div>
            <div className="top-cost-list">
              {stats.topCostTasks.map((task, i) => (
                <div key={task.id} className="top-cost-item">
                  <span className="top-cost-rank">{i + 1}</span>
                  <div className="top-cost-info">
                    <div className="top-cost-name">
                      {task.isCustomTaskName ? task.customTaskName : task.taskName}
                    </div>
                    <div className="top-cost-meta">
                      {task.employeeNames?.join(', ') || t('dashboard.teamX', { size: task.teamSize })}
                      {task.roomName ? ` · ${task.roomName}` : ''}
                      {' · '}{formatDuration(task.durationMinutes)}
                      {task.labourHours != null ? ` · ${task.labourHours.toFixed(1)}${t('dashboard.hLabour')}` : ''}
                    </div>
                  </div>
                  <div className="top-cost-amount">
                    <span className="top-cost-value">{formatCurrency(task.labourCost, currency)}</span>
                    {task.costPerUnit != null ? (
                      <span className="top-cost-unit">{formatCurrency(task.costPerUnit, currency)}{t('dashboard.perUnitSuffix')}</span>
                    ) : (
                      <span className="top-cost-unit top-cost-unit-empty">{t('dashboard.noUnitData')}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ Completed tasks — searchable, filterable, sortable ══ */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">{filters.statusScope === 'active' ? t('admin.activeTasks') : t('admin.completedTasks')}</span>
            <span className="text-muted text-sm">{t('productionMeta.taskCount', { count: tableRows.length })}</span>
          </div>

          <div className="table-toolbar">
            <div className="search-box" style={{ minWidth: 280 }}>
              <Search size={14} />
              <input
                className="search-input"
                placeholder={t('dashboard.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => handleFiltersChange({ ...filters, search: e.target.value })}
              />
              {filters.search && (
                <button
                  className="btn btn-icon"
                  style={{ padding: '2px 4px', color: 'var(--text-muted)' }}
                  onClick={() => handleFiltersChange({ ...filters, search: '' })}
                >
                  ×
                </button>
              )}
            </div>
            <div className="filter-group">
              {['all', 'production', 'support', 'delay'].map((type) => (
                <button
                  key={type}
                  className={`btn btn-sm ${filters.taskType === type ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => handleFiltersChange({ ...filters, taskType: type })}
                >
                  {type === 'all' ? t('common.all') : t(`categories.${type}`)}
                </button>
              ))}
            </div>
          </div>

          {tableLoading ? (
            <div className="page-loading" style={{ minHeight: 120 }}>
              <div className="spinner" />
            </div>
          ) : tableRows.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-8)' }}>
              <p className="text-sm">
                {baseTasks.length > 0 && filtersActive
                  ? t('analyticsViews.noFilterResults')
                  : filters.statusScope === 'active'
                  ? t('analyticsViews.noActiveTasks')
                  : filters.search || filters.taskType !== 'all'
                  ? t('common.noResults')
                  : datePreset === 'today'
                  ? t('dashboard.emptyToday')
                  : t('dashboard.emptyPeriod')}
              </p>
              {staleFilterHints.length > 0 && (
                <p className="text-xs text-muted" style={{ marginTop: 8 }}>
                  {t('analyticsViews.staleFilterHint')}
                </p>
              )}
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table">
                <thead>
                  <tr>
                    <Th label={t('dashboard.task')} col="taskName" {...thProps} />
                    <th>{t('dashboard.type')}</th>
                    <Th label={t('dashboard.durationLabel')} col="durationMinutes" {...thProps} right />
                    <Th label={t('dashboard.labourHrsCol')} col="labourHours" {...thProps} right />
                    <Th label={t('dashboard.labourCostCol')} col="labourCost" {...thProps} right />
                    <Th label={t('dashboard.costPerUnitLabel')} col="costPerUnit" {...thProps} right />
                    <th>{t('dashboard.downtimeCol')}</th>
                    <th>{t('dashboard.productLotCol')}</th>
                    <Th label={t('dashboard.qtyCol')} col="quantityCompleted" {...thProps} right />
                    <th>{t('dashboard.room')}</th>
                    <th>{t('dashboard.assignedCol')}</th>
                    <Th
                      label={filters.statusScope === 'active' ? t('dashboard.started') : t('dashboard.completedAt')}
                      col={filters.statusScope === 'active' ? 'startTime' : 'submittedAt'}
                      {...thProps}
                    />
                  </tr>
                </thead>
                <tbody>
                  {tableRows.slice(0, 100).map((task) => {
                    const displayName = task.isCustomTaskName ? task.customTaskName : task.taskName;
                    return (
                    <tr key={task.id}>
                      <td>
                        <div className="font-semibold text-sm">{displayName}</div>
                        {task.taskCategoryName
                          && task.taskCategoryName.toLowerCase() !== displayName?.toLowerCase() && (
                          <div className="text-xs text-muted">{task.taskCategoryName}</div>
                        )}
                      </td>
                      <td><span className={`badge badge-${task.taskType}`}>{task.taskType}</span></td>
                      <td className="text-right">
                        {formatDuration(task.durationMinutes)}
                        {isVeryShortCompletedTask(task) && (
                          <div className="text-xs text-muted" title={t('dashboard.veryShortTitle')}>{t('dashboard.veryShort')}</div>
                        )}
                      </td>
                      <td className="text-right">
                        {task.labourHours != null ? `${task.labourHours.toFixed(2)}h` : '—'}
                      </td>
                      <td className="text-right">
                        {task.labourCost != null ? (
                          <span className="font-semibold">{formatCurrency(task.labourCost, currency)}</span>
                        ) : task.labourCostCalculatedAt == null ? (
                          <span className="text-muted text-xs">{t('dashboard.pending')}</span>
                        ) : (
                          <span className="text-muted text-xs">{t('common.na')}</span>
                        )}
                      </td>
                      <td className="text-right text-sm">
                        {task.costPerUnit != null
                          ? formatCurrency(task.costPerUnit, currency)
                          : <span className="text-muted text-xs">{t('dashboard.noUnitData')}</span>}
                      </td>
                      <td className="text-sm">
                        {task.downtimeMinutes > 0 ? (
                          <span style={{ color: 'var(--color-warning)' }}>{task.downtimeMinutes}m</span>
                        ) : <span className="text-muted text-xs">{t('dashboard.none')}</span>}
                      </td>
                      <td className="text-sm">
                        {task.productName && <div>{task.productName}</div>}
                        {task.lotNumber && <div className="text-muted">{t('dashboard.lotPrefix', { number: task.lotNumber })}</div>}
                        {!task.productName && !task.lotNumber && (
                          <span className="text-muted text-xs">{t('dashboard.noLotAssigned')}</span>
                        )}
                      </td>
                      <td className="text-right text-sm">
                        {task.quantityCompleted != null
                          ? `${task.quantityCompleted} ${task.unitType || ''}`
                          : '—'}
                      </td>
                      <td className="text-sm">{task.roomName || '—'}</td>
                      <td className="text-sm">
                        {task.employeeNames?.join(', ') || t('dashboard.teamX', { size: task.teamSize })}
                      </td>
                      <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>
                        {formatSmartDateTime(
                          filters.statusScope === 'active'
                            ? (task.startTime?.toDate?.() ?? task.startTime)
                            : (task.submittedAt?.toDate?.() ?? task.submittedAt)
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {tableRows.length > 100 && (
                <div className="table-footer-note">
                  {t('dashboard.showingTasks', { total: tableRows.length })}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
